// imports
import React, { useState, useEffect, ReactElement } from 'react';
import dayjs, { Dayjs } from 'dayjs';

// lib files
import { formatDuration } from '@/lib/helpers/multi-livestream/formatDuration';
import { getListingGridColumnEndSpan } from '@/lib/helpers/multi-livestream/getListingGridColumnEndSpan';
import { getTimeRemaining } from '@/lib/helpers/multi-livestream/getTimeRemaining';
import { Listing, ListingPositionEnum, Theme } from '@/lib/types/api/multi-livestream';

// components
import MultiLivestreamListingModal from '@/components/MultiLivestream/Schedule/ListingModal';

// svgs
import KabobMenu from '@/public/svg/kabob.svg';
import PlayIcon from '@/public/svg/play.svg';

// styles
import styles from '@/components/MultiLivestream/Schedule/Listing.module.scss';

type MultiLivestreamListingProps = {
  channelCid: string;
  channelIsSelected: boolean;
  channelLogo: string;
  channelTitle: string;
  setUserSelectedChannel: React.Dispatch<React.SetStateAction<string>>;
  listing: Listing;
  listingIndex: number;
  now: Dayjs;
  scheduleStart: Dayjs;
  scheduleEnd: Dayjs;
  theme: Theme;
};

const MultiLivestreamListing = (
  props: MultiLivestreamListingProps
): ReactElement => {
  const {
    channelCid,
    channelIsSelected,
    channelLogo,
    channelTitle,
    setUserSelectedChannel,
    listing,
    listingIndex,
    now,
    scheduleStart,
    scheduleEnd,
    theme,
  } = props;
  const startTime = dayjs(listing.start_time);
  const endTime = startTime.add(listing.duration, 'second');
  const isAiringNow = now.isAfter(startTime) && now.isBefore(endTime);
  const timeRemaining = getTimeRemaining(now, endTime);
  const formattedDuration = formatDuration(listing.duration);
  // for most listings we want a grid-column-start of 'auto'
  // the only exception is if the first listing starts after the schedule
  const [gridColumnStart, setGridColumnStart] = useState('auto');
  const [gridColumnEndSpan, setGridColumnEndSpan] = useState('');
  const [listingProgress, setListingProgress] = useState(0);
  const listingPosition = listing.listing_position;
  const [isVisibleButInPast, setIsVisibleButInPast] = useState(
    listingPosition === ListingPositionEnum.startsAtScheduleStartAndIsEnded
  );
  const [modal, setModal] = useState(false);

  // setting the duration in minutes
  const listingDuration = Math.round(listing.duration / 60);

  useEffect(() => {
    const getGridColumn = () => {
      const span = getListingGridColumnEndSpan(
        listingPosition!,
        scheduleStart,
        scheduleEnd,
        startTime,
        endTime,
        listingDuration
      );

      setGridColumnEndSpan(span);

      // this covers the case where we have the first listing
      // BUT it starts after the schedule window - this is most common:
      // - with NHK
      // - in a future date at midnight when the first program had started prior
      // to midnight, in which case CS doesn't inlude it in the day's listings
      // so the first listing starts at some point after midnight
      if (
        listingPosition === ListingPositionEnum.duringSchedule &&
        listingIndex === 0
      ) {
        // get scheduleStart in whole minutes, *leaving out seconds*
        // because seconds will mess with the diff calculation,
        // which will then cause the grid to by off.
        const startTimeInMinutes = scheduleStart.startOf('minute');
        const diff = startTime.diff(startTimeInMinutes, 'minute');
        // if this is the first listing, but not at the start of the schedule,
        // we need one extra column to account for the lack of a listing
        // before this one
        setGridColumnStart((diff + 1).toString());
      }
    };

    getGridColumn();
  }, [
    listingPosition,
    listingIndex,
    scheduleStart,
    scheduleEnd,
    startTime,
    endTime,
    listingDuration,
  ]);

  useEffect(() => {
    const getListingProgress = () => {
      if (isAiringNow) {
        // sometimes the start time is after the schedule start
        // in cases like nhk where it's a 15 minute segment that starts at :15,
        // and we're at :16, but we're still displaying back to :00
        const whichIsLater = startTime.isAfter(scheduleStart)
          ? startTime
          : scheduleStart;
        const timeElapsed = now.diff(whichIsLater, 'minute');
        setListingProgress(timeElapsed);
      }

      if (now.isAfter(endTime)) {
        setIsVisibleButInPast(true);
      }
    };

    getListingProgress();
  }, [now, isAiringNow, scheduleStart, startTime, endTime]);

  let classNames = styles.listing;

  if (isAiringNow) {
    classNames += ` ${styles.airing_now}`;
  }

  if (channelIsSelected) {
    classNames += ` ${styles.selected_channel}`;
  }

  if (isVisibleButInPast) {
    classNames += ` ${styles.is_past}`;
  }

  const remaingingOrDuration =
    isAiringNow && timeRemaining ? timeRemaining : formattedDuration;

  const canSwitchToThisChannel = isAiringNow && !channelIsSelected;

  const showTitle = listing.show_title;
  const episodeTitle = listing.episode_title;
  const episodeDescription = listing.episode_description;

  const listingDetails: ReactElement = (
    <>
      {isAiringNow && channelIsSelected && (
        <span className={styles.listing__livestream_bug} aria-hidden="true">
          Now Playing
        </span>
      )}
      <p className={styles.listing__show_title}>{showTitle}</p>
      <p className={styles.listing__duration}>{remaingingOrDuration}</p>
      {canSwitchToThisChannel && (
        <span className={styles.listing__watch_now}>
          <PlayIcon className={styles.listing__watch_now__play_icon} />
          <span className="visuallyhidden">Watch Now</span>
        </span>
      )}
    </>
  );

  return (
    <div
      className={classNames}
      style={{
        gridColumn: `${gridColumnStart} / span ${gridColumnEndSpan}`,
        // subgrid doesn't work on listing, so manually defining columns
        gridTemplateColumns: `repeat(${gridColumnEndSpan}, 1fr)`,
      }}
      title={`${listing.show_title} | ${remaingingOrDuration}`}
    >
      {canSwitchToThisChannel ? (
        <button
          className={`${styles.listing__details} ${styles.listing__details__button}`}
          onClick={() => setUserSelectedChannel(channelCid)}
          data-testid="listing-details-button"
        >
          {listingDetails}
        </button>
      ) : (
        <div className={styles.listing__details}>{listingDetails}</div>
      )}

      <button className={styles.listing__modal_trigger} onClick={() => setModal(true)}>
        <KabobMenu className={styles.listing__kabob_icon} />
        <span className="visuallyhidden">
          More information about this listing
        </span>
      </button>

      <MultiLivestreamListingModal
        openModal={modal}
        closeModal={() => setModal(false)}
        channelCid={channelCid}
        channelLogo={channelLogo}
        channelTitle={channelTitle}
        channelIsSelected={channelIsSelected}
        setUserSelectedChannel={setUserSelectedChannel}
        showTitle={showTitle}
        episodeTitle={episodeTitle}
        episodeDescription={episodeDescription}
        isAiringNow={isAiringNow}
        timeRemaining={timeRemaining!}
        listingProgress={listingProgress}
        listingDuration={listingDuration}
        formattedDuration={formattedDuration}
        theme={theme}
      />

      {isAiringNow && listingProgress !== 0 && (
        <div
          className={styles.listing__progress_bar}
          aria-hidden="true"
          style={{
            gridColumnEnd: `span ${listingProgress}`,
          }}
        ></div>
      )}
    </div>
  );
};

export default MultiLivestreamListing;
